import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Layout, Tree, Button, Input, Space, Modal, Form,
  message, Popconfirm, Card, Row, Col, Grid, Tooltip,
  Dropdown, Breadcrumb, Empty, Table, Typography,
} from 'antd'
import type { MenuProps } from 'antd'
import type { DataNode, TreeProps } from 'antd/es/tree'
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, FolderOutlined, FolderOpenOutlined, FileTextOutlined,
  MoreOutlined, HomeOutlined,
} from '@ant-design/icons'
import MDEditor from '@uiw/react-md-editor'
import {
  getSocialMediaCommands,
  createSocialMediaCommand,
  updateSocialMediaCommand,
  deleteSocialMediaCommand,
  getFolderTree,
  createFolder,
  updateFolder,
  deleteFolder,
  type SocialMediaCommand,
  type FolderTreeNode,
} from '../../../../api/social-commands'
import dayjs from 'dayjs'

const { Sider, Content } = Layout
const { Text } = Typography

interface TreeDataNode extends DataNode {
  key: string
  title: React.ReactNode
  children?: TreeDataNode[]
  isLeaf?: boolean
}

function buildTreeData(nodes: FolderTreeNode[]): TreeDataNode[] {
  return nodes.map((node) => ({
    key: node.id,
    title: node.name,
    icon: ({ expanded }: { expanded?: boolean }) =>
      expanded ? <FolderOpenOutlined /> : <FolderOutlined />,
    children: node.children?.length ? buildTreeData(node.children) : undefined,
  }))
}

export default function SocialCommandsPage() {
  const [items, setItems] = useState<SocialMediaCommand[]>([])
  const [loading, setLoading] = useState(false)
  const [treeData, setTreeData] = useState<TreeDataNode[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [searchText, setSearchText] = useState('')

  const [modalVisible, setModalVisible] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewFullscreen, setPreviewFullscreen] = useState(false)
  const [previewItem, setPreviewItem] = useState<SocialMediaCommand | null>(null)
  const [editingItem, setEditingItem] = useState<SocialMediaCommand | null>(null)
  const [form] = Form.useForm()

  const [folderModalVisible, setFolderModalVisible] = useState(false)
  const [folderForm] = Form.useForm()
  const [editingFolder, setEditingFolder] = useState<{ id: string; name: string } | null>(null)
  const [parentFolderId, setParentFolderId] = useState<string | null>(null)
  const [dragItemId, setDragItemId] = useState<string | null>(null)
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null)

  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const fetchFolders = useCallback(async () => {
    try {
      const data = await getFolderTree()
      setTreeData(buildTreeData(data))
    } catch (err: any) {
      message.error(err.message || '加载文件夹失败')
    }
  }, [])

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getSocialMediaCommands(
        searchText || undefined,
        selectedFolderId ?? '',
      )
      setItems(data)
    } finally {
      setLoading(false)
    }
  }, [searchText, selectedFolderId])

  useEffect(() => {
    fetchFolders()
  }, [fetchFolders])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const breadcrumbPath = useMemo(() => {
    const path: { id: string | null; name: string }[] = [{ id: null, name: '根目录' }]
    if (!selectedFolderId || treeData.length === 0) return path

    function findPath(nodes: TreeDataNode[], target: string): TreeDataNode[] | null {
      for (const node of nodes) {
        if (node.key === target) return [node]
        if (node.children) {
          const sub = findPath(node.children, target)
          if (sub) return [node, ...sub]
        }
      }
      return null
    }

    const found = findPath(treeData, selectedFolderId)
    if (found) {
      found.forEach((n) => path.push({ id: String(n.key), name: String(n.title) }))
    }
    return path
  }, [selectedFolderId, treeData])

  const currentFolderName = useMemo(() => {
    if (!selectedFolderId) return '全部指令'
    const last = breadcrumbPath[breadcrumbPath.length - 1]
    return last?.name || '当前文件夹'
  }, [selectedFolderId, breadcrumbPath])

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        name: values.name,
        content: values.content || undefined,
        folderId: selectedFolderId || undefined,
      }
      if (editingItem) {
        await updateSocialMediaCommand(editingItem.id, payload)
        message.success('更新成功')
      } else {
        await createSocialMediaCommand(payload)
        message.success('创建成功')
      }
      setModalVisible(false)
      setEditingItem(null)
      form.resetFields()
      fetchItems()
    } catch (err: any) {
      message.error(err.message || '操作失败')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteSocialMediaCommand(id)
      message.success('删除成功')
      fetchItems()
    } catch (err: any) {
      message.error(err.message || '删除失败')
    }
  }

  const openEdit = (item: SocialMediaCommand) => {
    setEditingItem(item)
    form.setFieldsValue({
      name: item.name,
      content: item.content || '',
    })
    setModalVisible(true)
  }

  const openCreate = () => {
    setEditingItem(null)
    form.resetFields()
    setModalVisible(true)
  }

  const openPreview = (item: SocialMediaCommand) => {
    setPreviewItem(item)
    setPreviewVisible(true)
  }

  const handleCreateFolder = async (values: { name: string }) => {
    try {
      await createFolder({
        name: values.name,
        parentId: parentFolderId || undefined,
      })
      message.success('文件夹创建成功')
      setFolderModalVisible(false)
      folderForm.resetFields()
      setParentFolderId(null)
      fetchFolders()
    } catch (err: any) {
      message.error(err.message || '创建失败')
    }
  }

  const handleUpdateFolder = async (values: { name: string }) => {
    if (!editingFolder) return
    try {
      await updateFolder(editingFolder.id, { name: values.name })
      message.success('重命名成功')
      setFolderModalVisible(false)
      folderForm.resetFields()
      setEditingFolder(null)
      fetchFolders()
    } catch (err: any) {
      message.error(err.message || '重命名失败')
    }
  }

  const handleDeleteFolder = async (id: string) => {
    try {
      await deleteFolder(id)
      message.success('删除成功')
      if (selectedFolderId === id) {
        setSelectedFolderId(null)
      }
      fetchFolders()
    } catch (err: any) {
      message.error(err.message || '删除失败')
    }
  }

  const openCreateFolder = (parentId?: string) => {
    setEditingFolder(null)
    setParentFolderId(parentId || null)
    folderForm.resetFields()
    setFolderModalVisible(true)
  }

  const openEditFolder = (folder: { id: string; name: string }) => {
    setEditingFolder(folder)
    setParentFolderId(null)
    folderForm.setFieldsValue({ name: folder.name })
    setFolderModalVisible(true)
  }

  const onSelect: TreeProps['onSelect'] = (keys) => {
    const key = keys[0] as string | undefined
    setSelectedFolderId(key ?? null)
  }

  const onExpand: TreeProps['onExpand'] = (keys) => {
    setExpandedKeys(keys)
  }

  const renderTreeTitle = (node: TreeDataNode) => {
    const folderId = String(node.key)
    const folderName = String(node.title)
    const isSelected = selectedFolderId === folderId
    const isDragOver = dragOverFolderId === folderId

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      if (dragOverFolderId !== folderId) {
        setDragOverFolderId(folderId)
      }
    }

    const handleDragLeave = (e: React.DragEvent) => {
      const related = e.relatedTarget as HTMLElement | null
      if (!related?.closest(`[data-folder-id="${folderId}"]`)) {
        setDragOverFolderId((prev) => (prev === folderId ? null : prev))
      }
    }

    const handleDrop = async (e: React.DragEvent) => {
      e.preventDefault()
      const itemId = e.dataTransfer.getData('text/plain')
      setDragOverFolderId(null)
      setDragItemId(null)

      if (!itemId) return

      const item = items.find((i) => i.id === itemId)
      if (item?.folderId === folderId) return

      try {
        await updateSocialMediaCommand(itemId, { folderId })
        message.success('移动成功')
        fetchItems()
      } catch (err: any) {
        message.error(err.message || '移动失败')
      }
    }

    const menuItems: MenuProps['items'] = [
      {
        key: 'add',
        label: 'New Subfolder',
        icon: <PlusOutlined />,
        onClick: () => openCreateFolder(folderId),
      },
      {
        key: 'rename',
        label: '重命名',
        icon: <EditOutlined />,
        onClick: () => openEditFolder({ id: folderId, name: folderName }),
      },
      {
        key: 'delete',
        label: '删除',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          Modal.confirm({
            title: '确认删除文件夹',
            content: `删除「${folderName}」，该文件夹必须为空才能删除。`,
            okText: '删除',
            okType: 'danger',
            onOk: () => handleDeleteFolder(folderId),
          })
        },
      },
    ]

    return (
      <span
        data-folder-id={folderId}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: 'calc(100% - 24px)',
          backgroundColor: isDragOver ? '#e6f4ff' : 'transparent',
          transition: 'background-color 0.15s',
          borderRadius: 4,
          padding: '2px 0',
        }}
        onClick={(e) => e.stopPropagation()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <span
          style={{
            cursor: 'pointer',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: isSelected ? 500 : 400,
            color: isSelected ? '#1677ff' : 'inherit',
          }}
          onClick={() => {
            setSelectedFolderId(folderId)
            if (!expandedKeys.includes(folderId) && node.children && node.children.length > 0) {
              setExpandedKeys([...expandedKeys, folderId])
            }
          }}
        >
          {folderName}
        </span>
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            style={{ marginLeft: 4, opacity: 0.6 }}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      </span>
    )
  }

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (v: string) => (
        <Space>
          <FileTextOutlined style={{ color: '#bfbfbf' }} />
          <span style={{ fontWeight: 500 }}>{v}</span>
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      align: 'center' as const,
      render: (_: any, record: SocialMediaCommand) => (
        <Space>
          <Tooltip title="预览">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => openPreview(record)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除"
            description="删除后不可恢复，是否继续？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Layout style={{ minHeight: 'calc(100vh - 180px)', background: '#fff' }}>
      <Sider
        width={240}
        theme="light"
        style={{
          background: '#f8f9fa',
          overflow: 'auto',
          display: isMobile ? 'none' : 'block',
        }}
      >
        <div style={{ padding: '16px 12px 12px' }}>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            block
            onClick={() => openCreateFolder()}
          >
            New Folder
          </Button>
        </div>
        <Tree
          showIcon
          blockNode
          selectedKeys={selectedFolderId ? [selectedFolderId] : []}
          expandedKeys={expandedKeys}
          onSelect={onSelect}
          onExpand={onExpand}
          treeData={treeData.map((node) => ({
            ...node,
            title: renderTreeTitle(node),
            children: node.children?.map((child) => ({
              ...child,
              title: renderTreeTitle(child),
              children: child.children?.map((c2) => ({
                ...c2,
                title: renderTreeTitle(c2),
              })),
            })),
          }))}
          style={{ padding: '0 8px 8px', background: 'transparent' }}
        />
      </Sider>

      <Content style={{ padding: '20px 24px', minWidth: 0, background: '#fff' }}>
        <Breadcrumb
          style={{ marginBottom: 20 }}
          items={breadcrumbPath.map((crumb, idx) => ({
            key: String(crumb.id),
            title: idx === 0
              ? (
                <a onClick={() => setSelectedFolderId(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <HomeOutlined /> {crumb.name}
                </a>
              )
              : (
                <a onClick={() => setSelectedFolderId(crumb.id)}>{crumb.name}</a>
              ),
          }))}
        />

        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Text strong style={{ fontSize: 18 }}>{currentFolderName}</Text>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              共 {items.length} 条指令
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新增指令
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <Input
            placeholder="搜索指令名称"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={fetchItems}
            style={{ width: isMobile ? '100%' : 280 }}
            allowClear
          />
          <Button onClick={fetchItems}>查询</Button>
        </div>

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: false, simple: true }}
          scroll={{ x: isMobile ? 600 : undefined }}
          locale={{ emptyText: <Empty description="暂无指令" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          onRow={(record) => ({
            draggable: true,
            onDragStart: (e) => {
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', record.id)
              setDragItemId(record.id)
            },
            onDragEnd: () => {
              setDragItemId(null)
              setDragOverFolderId(null)
            },
            style: { cursor: 'grab' },
          })}
        />
      </Content>

      {/* 编辑/新建 Modal */}
      <Modal
        title={editingItem ? '编辑md文件归档' : '新增md文件归档'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false)
          setEditingItem(null)
          form.resetFields()
        }}
        width={isMobile ? '95%' : 720}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="指令名称"
            rules={[{ required: true, message: '请输入指令名称' }]}
          >
            <Input placeholder="输入md文件归档名称" />
          </Form.Item>
          <Form.Item
            name="content"
            label="指令内容（支持 Markdown）"
          >
            <MDEditor
              height={360}
              visibleDragbar={false}
              data-color-mode="light"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览 Modal */}
      <Modal
        title={
          <Space>
            <span>{previewItem?.name || '预览'}</span>
            {!isMobile && (
              <Button
                type="link"
                size="small"
                onClick={() => setPreviewFullscreen((v) => !v)}
              >
                {previewFullscreen ? '退出全屏' : '全屏'}
              </Button>
            )}
          </Space>
        }
        open={previewVisible}
        onOk={() => setPreviewVisible(false)}
        onCancel={() => {
          setPreviewVisible(false)
          setPreviewFullscreen(false)
        }}
        width={isMobile ? '95%' : previewFullscreen ? '95%' : 960}
        style={{ maxWidth: previewFullscreen ? '100%' : undefined }}
        styles={{
          body: {
            maxHeight: '80vh',
            overflow: 'auto',
            padding: isMobile ? '12px 16px' : '20px 28px',
          },
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => setPreviewVisible(false)}>
            关闭
          </Button>,
        ]}
        destroyOnClose
      >
        {previewItem?.content ? (
          <MDEditor.Markdown
            source={previewItem.content}
            style={{
              background: 'transparent',
              lineHeight: 1.8,
              fontSize: 15,
            }}
          />
        ) : (
          <div style={{ color: '#999', textAlign: 'center', padding: 40 }}>
            暂无内容
          </div>
        )}
      </Modal>

      {/* 文件夹 Modal */}
      <Modal
        title={editingFolder ? 'Rename Folder' : 'New Folder'}
        open={folderModalVisible}
        onOk={() => folderForm.submit()}
        onCancel={() => {
          setFolderModalVisible(false)
          setEditingFolder(null)
          setParentFolderId(null)
          folderForm.resetFields()
        }}
        destroyOnClose
      >
        <Form
          form={folderForm}
          layout="vertical"
          onFinish={editingFolder ? handleUpdateFolder : handleCreateFolder}
        >
          <Form.Item
            name="name"
            label="Folder Name"
            rules={[{ required: true, message: 'Please enter a folder name' }]}
          >
            <Input placeholder="Enter folder name" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}
